import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus,
  Copy,
  RefreshCw,
  Shield,
  Trash2,
  Pencil,
  Building2,
  Users,
  Info,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Layers,
  Loader2,
} from "lucide-react";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { getDepartmentsByBranch } from "../../../api/hrm/departments/departmentInfo";
import {
  createUsageRole,
  deleteUsageRole,
  getUsageRolePresets,
  getUsageRoleStats,
  getUsageRoleLogs,
  getUsageRoles,
  propagateUsageRole,
  updateUsageRole,
} from "../../../api/settings/usageRoles";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import {
  ACTION_LABELS,
  FEATURE_GROUP_CATALOG,
  MODULE_CATEGORIES,
  SCOPE_LEVEL_OPTIONS,
  SOURCE_MODE_OPTIONS,
  expandLegacyGrants,
} from "../../../config/featureGroups";
import { refreshSessionPermissions } from "../../../utils/refreshSessionPermissions";
import UsageRoleStatsBar from "./UsageRoleStatsBar";
import UsageRoleLogsPanel from "./UsageRoleLogsPanel";

const EMPTY_GRANTS = () =>
  Object.fromEntries(FEATURE_GROUP_CATALOG.map((g) => [g.key, []]));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Overlay spinner + thanh tiến trình 0–100% đè lên modal. */
const BusyProgressOverlay = ({ open, percent = 0, title, detail }) => {
  if (!open) return null;
  const pct = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));
  return (
    <div className="absolute inset-0 z-[60] flex items-center justify-center bg-white/80 backdrop-blur-[2px] rounded-t-2xl sm:rounded-2xl">
      <div className="w-[min(340px,90%)] mx-4 rounded-2xl border border-gray-100 bg-white shadow-xl px-5 py-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
        <p className="text-sm font-bold text-gray-800">{title || "Đang xử lý..."}</p>
        {detail ? <p className="mt-1 text-xs text-gray-500">{detail}</p> : null}
        <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-red-600 transition-[width] duration-200 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-2 text-sm font-black tabular-nums text-red-600">{pct}%</p>
      </div>
    </div>
  );
};

/**
 * Chạy async work kèm progress giả lập (tăng dần tới ~90%, hoàn tất = 100%).
 * Dùng cho 1 API call không có tiến trình thật.
 */
async function runWithSimulatedProgress(work, setBusy, { title, detail } = {}) {
  let current = 6;
  let stopped = false;
  setBusy({ open: true, percent: current, title, detail });

  const timer = window.setInterval(() => {
    if (stopped) return;
    current = Math.min(current + 3 + Math.random() * 7, 90);
    setBusy((prev) =>
      prev?.open
        ? {
            ...prev,
            percent: current,
            detail:
              current < 35
                ? "Đang kiểm tra dữ liệu..."
                : current < 70
                  ? "Đang lưu cấu hình quyền..."
                  : "Đang đồng bộ phân quyền hệ thống...",
          }
        : prev
    );
  }, 220);

  try {
    const result = await work();
    stopped = true;
    window.clearInterval(timer);
    setBusy((prev) => ({
      ...(prev || {}),
      open: true,
      percent: 100,
      title: title || "Hoàn tất",
      detail: "Đã lưu thành công",
    }));
    await sleep(320);
    return result;
  } catch (err) {
    stopped = true;
    window.clearInterval(timer);
    throw err;
  } finally {
    stopped = true;
    window.clearInterval(timer);
    setBusy({ open: false, percent: 0, title: "", detail: "" });
  }
}

const FormSection = ({ step, title, hint, children, disabled }) => (
  <section
    className={`rounded-2xl border p-4 space-y-3 ${
      disabled ? "border-gray-100 bg-gray-50/80 opacity-60 pointer-events-none" : "border-gray-200 bg-white"
    }`}
  >
    <div className="flex items-start gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-black text-white">
        {step}
      </span>
      <div>
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        {hint && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{hint}</p>}
      </div>
    </div>
    {children}
  </section>
);

const HintBox = ({ children }) => (
  <div className="flex gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs text-blue-900 leading-relaxed">
    <Info className="h-4 w-4 shrink-0 mt-0.5 text-blue-600" />
    <div>{children}</div>
  </div>
);

const stripSearchText = (input) =>
  String(input ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

const UsageRoleFormModal = ({
  open,
  onClose,
  onSaved,
  editing,
  cloneSource,
  presets,
}) => {
  const [label, setLabel] = useState("");
  const [branchId, setBranchId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [scopeLevel, setScopeLevel] = useState("self");
  const [source, setSource] = useState("preset");
  const [presetCode, setPresetCode] = useState("");
  const [cloneFromId, setCloneFromId] = useState("");
  const [cloneRoles, setCloneRoles] = useState([]);
  const [cloneRolesLoading, setCloneRolesLoading] = useState(false);
  const [grants, setGrants] = useState(EMPTY_GRANTS());
  const [moduleScopes, setModuleScopes] = useState({});
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState({
    open: false,
    percent: 0,
    title: "",
    detail: "",
  });
  const [expandedCats, setExpandedCats] = useState(() =>
    Object.fromEntries(MODULE_CATEGORIES.map((c) => [c.key, true]))
  );

  const orgReady = !!branchId;
  const selectedPreset = presets.find((p) => p.code === presetCode);
  const cloneOptions = useMemo(() => {
    return cloneRoles
      .filter((r) => r.is_active !== false)
      .filter((r) => !editing || r.id !== editing.id)
      .map((r) => {
        const roleName = r.label || "—";
        const deptName = r.department_name || "Mọi bộ phận";
        const branchName = r.branch_name || "Toàn hệ thống";
        return {
          value: r.id,
          // Chuỗi đầy đủ: quyền - chức vụ/BP - chi nhánh
          label: `${roleName} - ${deptName} - ${branchName}`,
          roleName,
          deptName,
          branchName,
          searchText: stripSearchText(`${roleName} ${deptName} ${branchName}`),
        };
      });
  }, [cloneRoles, editing]);

  const syncScopesWithGrants = (nextGrants, defaultScope, previousScopes = {}) => {
    const next = {};
    Object.entries(nextGrants || {}).forEach(([key, acts]) => {
      if (Array.isArray(acts) && acts.length > 0) {
        next[key] = previousScopes[key] || defaultScope || "self";
      }
    });
    return next;
  };

  useEffect(() => {
    if (!open) return;
    getAllBranches_Array().then((rows) =>
      setBranches(rows.map((b) => ({ value: b.id, label: b.name })))
    );
  }, [open]);

  // Tải TOÀN BỘ quyền hệ thống cho dropdown sao chép (không phụ thuộc lọc CN/BP trên list)
  useEffect(() => {
    if (!open || editing) {
      setCloneRoles([]);
      return;
    }
    let cancelled = false;
    setCloneRolesLoading(true);
    getUsageRoles({})
      .then((res) => {
        if (!cancelled) setCloneRoles(res?.data || []);
      })
      .catch(() => {
        if (!cancelled) setCloneRoles([]);
      })
      .finally(() => {
        if (!cancelled) setCloneRolesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, editing]);

  useEffect(() => {
    if (!branchId) {
      setDepartments([]);
      return;
    }
    getDepartmentsByBranch(branchId).then((rows) =>
      setDepartments(rows.map((d) => ({ value: d.id, label: d.name })))
    );
  }, [branchId]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const nextGrants = {
        ...EMPTY_GRANTS(),
        ...expandLegacyGrants(editing.feature_grants || {}),
      };
      const nextScope = editing.scope_level || "self";
      setLabel(editing.label || "");
      setBranchId(editing.branch_id || "");
      setDepartmentId(editing.department_id || "");
      setScopeLevel(nextScope);
      setGrants(nextGrants);
      setModuleScopes(
        syncScopesWithGrants(nextGrants, nextScope, editing.module_scopes || {})
      );
      setSource("manual");
      return;
    }
    setLabel("");
    setBranchId(cloneSource?.branch_id || "");
    setDepartmentId(cloneSource?.department_id || "");
    setScopeLevel("self");
    setGrants(EMPTY_GRANTS());
    setModuleScopes({});
    setSource(cloneSource ? "clone" : "preset");
    setPresetCode(presets[0]?.code || "");
    setCloneFromId(cloneSource?.id || "");
  }, [open, editing, cloneSource, presets]);

  useEffect(() => {
    if (!open || editing || source !== "preset" || !presetCode) return;
    const preset = presets.find((p) => p.code === presetCode);
    if (preset) {
      const nextScope = preset.default_scope_level || "self";
      const nextGrants = {
        ...EMPTY_GRANTS(),
        ...expandLegacyGrants(preset.feature_grants || {}),
      };
      setScopeLevel(nextScope);
      setGrants(nextGrants);
      setModuleScopes(syncScopesWithGrants(nextGrants, nextScope, {}));
    }
  }, [open, editing, source, presetCode, presets]);

  useEffect(() => {
    if (!open || editing || source !== "clone" || !cloneFromId) return;
    const src = cloneRoles.find((r) => r.id === cloneFromId);
    if (src) {
      const nextScope = src.scope_level || "self";
      const nextGrants = {
        ...EMPTY_GRANTS(),
        ...expandLegacyGrants(src.feature_grants || {}),
      };
      setScopeLevel(nextScope);
      setGrants(nextGrants);
      setModuleScopes(
        syncScopesWithGrants(nextGrants, nextScope, src.module_scopes || {})
      );
    }
  }, [open, editing, source, cloneFromId, cloneRoles]);

  const toggleAction = (groupKey, action) => {
    setGrants((prev) => {
      const current = new Set(prev[groupKey] || []);
      if (current.has(action)) current.delete(action);
      else current.add(action);
      const nextActs = [...current].sort();
      const next = { ...prev, [groupKey]: nextActs };
      setModuleScopes((scopes) => {
        const nextScopes = { ...scopes };
        if (nextActs.length) {
          nextScopes[groupKey] = scopes[groupKey] || scopeLevel;
        } else {
          delete nextScopes[groupKey];
        }
        return nextScopes;
      });
      return next;
    });
  };

  const setFeatureScope = (groupKey, level) => {
    setModuleScopes((prev) => ({ ...prev, [groupKey]: level || scopeLevel }));
  };

  const setCategoryScope = (catKey, level) => {
    setModuleScopes((prev) => {
      const next = { ...prev };
      FEATURE_GROUP_CATALOG.filter((g) => g.category === catKey).forEach((g) => {
        if ((grants[g.key] || []).length > 0) next[g.key] = level;
      });
      return next;
    });
  };

  const applyDefaultScopeToAll = (level) => {
    setScopeLevel(level);
    setModuleScopes((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        next[k] = level;
      });
      return next;
    });
  };

  const toggleCategory = (catKey) => {
    setExpandedCats((prev) => ({ ...prev, [catKey]: !prev[catKey] }));
  };

  const setCategoryAll = (catKey, on) => {
    setGrants((prev) => {
      const next = { ...prev };
      FEATURE_GROUP_CATALOG.filter((g) => g.category === catKey).forEach((g) => {
        next[g.key] = on ? [...g.actions] : [];
      });
      setModuleScopes((scopes) => {
        const nextScopes = { ...scopes };
        FEATURE_GROUP_CATALOG.filter((g) => g.category === catKey).forEach((g) => {
          if (on) nextScopes[g.key] = scopes[g.key] || scopeLevel;
          else delete nextScopes[g.key];
        });
        return nextScopes;
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!branchId) {
      showErrorToast("Vui lòng chọn chi nhánh trước");
      return;
    }
    if (!label.trim()) {
      showErrorToast("Vui lòng nhập tên quyền sử dụng");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: label.trim(),
        branch_id: branchId,
        department_id: departmentId || null,
        scope_level: scopeLevel,
        feature_grants: grants,
        module_scopes: syncScopesWithGrants(grants, scopeLevel, moduleScopes),
        source: editing ? "manual" : source === "manual" ? "manual" : source,
        preset_code: source === "preset" ? presetCode : null,
        clone_from_id: source === "clone" ? cloneFromId : null,
      };
      if (editing) {
        const updated = await runWithSimulatedProgress(
          () => updateUsageRole(editing.id, payload),
          setBusy,
          {
            title: "Đang lưu quyền sử dụng...",
            detail: "Vui lòng chờ, không đóng cửa sổ",
          }
        );
        showSuccessToast("Đã cập nhật quyền sử dụng");
        onSaved(updated);
      } else {
        await runWithSimulatedProgress(
          () => createUsageRole(payload),
          setBusy,
          {
            title: "Đang tạo quyền sử dụng...",
            detail: "Vui lòng chờ, không đóng cửa sổ",
          }
        );
        showSuccessToast("Đã tạo quyền sử dụng");
        onSaved();
      }
      onClose();
    } catch (err) {
      showErrorToast(err?.response?.data?.error || "Không lưu được");
    } finally {
      setSaving(false);
    }
  };

  const scopeHint = SCOPE_LEVEL_OPTIONS.find((o) => o.value === scopeLevel)?.hint;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="relative bg-gray-50 w-full sm:max-w-4xl max-h-[94vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-xl">
        <BusyProgressOverlay
          open={busy.open}
          percent={busy.percent}
          title={busy.title}
          detail={busy.detail}
        />
        <div className="sticky top-0 z-10 bg-white border-b px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-gray-800">
              {editing ? "Sửa quyền sử dụng" : "Thêm quyền sử dụng"}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Quyền gắn với chi nhánh & bộ phận — cấu hình một lần, gán cho nhiều NV.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving || busy.open}
            className="text-gray-400 hover:text-gray-600 text-lg disabled:opacity-40"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Bước 1: Đơn vị */}
          <FormSection
            step={1}
            title="Chọn đơn vị áp dụng"
            hint="Bắt buộc chọn chi nhánh. Bộ phận giúp lọc quyền khi HCNS gán cho nhân viên mới."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">
                  Chi nhánh <span className="text-red-500">*</span>
                </label>
                <Select
                  styles={customSelectStyles}
                  options={branches}
                  value={branches.find((b) => b.value === branchId) || null}
                  onChange={(opt) => {
                    setBranchId(opt?.value || "");
                    setDepartmentId("");
                  }}
                  placeholder="Chọn chi nhánh..."
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Bộ phận</label>
                <Select
                  styles={customSelectStyles}
                  isClearable
                  options={departments}
                  value={departments.find((d) => d.value === departmentId) || null}
                  onChange={(opt) => setDepartmentId(opt?.value || "")}
                  placeholder={branchId ? "Chọn bộ phận (tuỳ chọn)..." : "Chọn chi nhánh trước"}
                  isDisabled={!branchId}
                  className="mt-1"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Để trống = quyền áp dụng cả chi nhánh (VD: GĐ chi nhánh).
                </p>
              </div>
            </div>
          </FormSection>

          {/* Bước 2: Thông tin quyền */}
          <FormSection
            step={2}
            title="Thông tin quyền sử dụng"
            hint="Đặt tên dễ hiểu. Phạm vi mặc định áp dụng cho mọi chức năng; có thể chỉnh riêng từng màn hình ở bước quyền chi tiết."
            disabled={!orgReady}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">
                  Tên quyền <span className="text-red-500">*</span>
                </label>
                <input
                  className="mt-1 w-full border rounded-xl px-3 py-2.5 text-sm"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="VD: Trưởng phòng Kinh doanh"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">
                  Phạm vi mặc định
                </label>
                <Select
                  styles={customSelectStyles}
                  options={SCOPE_LEVEL_OPTIONS}
                  value={SCOPE_LEVEL_OPTIONS.find((o) => o.value === scopeLevel)}
                  onChange={(opt) => applyDefaultScopeToAll(opt?.value || "self")}
                  formatOptionLabel={(opt) => (
                    <div>
                      <span className="font-medium">{opt.label}</span>
                    </div>
                  )}
                  className="mt-1"
                />
                {scopeHint && (
                  <p className="text-[11px] text-gray-500 mt-1 flex items-start gap-1">
                    <Info size={12} className="shrink-0 mt-0.5" /> {scopeHint}
                  </p>
                )}
                <p className="text-[11px] text-amber-700 mt-1">
                  Đổi mặc định sẽ áp dụng lại cho tất cả chức năng đang bật.
                </p>
              </div>
            </div>
          </FormSection>

          {/* Bước 3: Nguồn khởi tạo */}
          {!editing && (
            <FormSection
              step={3}
              title="Khởi tạo quyền chức năng"
              hint="Chọn cách điền sẵn bảng quyền bên dưới. Bạn vẫn có thể chỉnh sau khi chọn."
              disabled={!orgReady}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SOURCE_MODE_OPTIONS.map((mode) => {
                  const active = source === mode.value;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setSource(mode.value)}
                      className={`text-left rounded-xl border p-3 transition-all ${
                        active
                          ? "border-red-400 bg-red-50 ring-2 ring-red-200"
                          : "border-gray-200 bg-white hover:border-red-200"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-bold text-gray-800">{mode.title}</span>
                        {mode.badge && (
                          <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded bg-red-600 text-white">
                            {mode.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">{mode.description}</p>
                    </button>
                  );
                })}
              </div>

              {source === "preset" && (
                <div className="space-y-2 pt-1">
                  <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
                    <Sparkles size={12} /> Chọn mẫu chức vụ
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {presets.map((p) => {
                      const active = presetCode === p.code;
                      return (
                        <button
                          key={p.code}
                          type="button"
                          onClick={() => setPresetCode(p.code)}
                          className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                            active ? "border-red-400 bg-red-50" : "border-gray-200 hover:border-red-200"
                          }`}
                        >
                          <p className="text-sm font-bold text-gray-800">{p.label_vi}</p>
                          {p.description_vi && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{p.description_vi}</p>
                          )}
                          <p className="text-[10px] text-red-600 font-bold mt-1 uppercase">
                            Phạm vi gợi ý:{" "}
                            {SCOPE_LEVEL_OPTIONS.find((s) => s.value === p.default_scope_level)?.label ||
                              p.default_scope_level}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                  {selectedPreset && (
                    <HintBox>
                      Đã chọn mẫu <strong>{selectedPreset.label_vi}</strong>. Các ô quyền bên dưới được
                      điền sẵn — bật/tắt thêm nếu cần.
                    </HintBox>
                  )}
                </div>
              )}

              {source === "clone" && (
                <div className="space-y-2 pt-1">
                  <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
                    <Copy size={12} /> Sao chép từ quyền đã có
                  </label>
                  <Select
                    styles={customSelectStyles}
                    options={cloneOptions}
                    value={cloneOptions.find((o) => o.value === cloneFromId) || null}
                    onChange={(opt) => setCloneFromId(opt?.value || "")}
                    placeholder="Tìm theo quyền, bộ phận hoặc chi nhánh..."
                    isSearchable
                    isClearable
                    isLoading={cloneRolesLoading}
                    noOptionsMessage={() =>
                      cloneRolesLoading
                        ? "Đang tải danh sách quyền..."
                        : "Không tìm thấy quyền phù hợp"
                    }
                    filterOption={(option, input) => {
                      if (!input?.trim()) return true;
                      return option.data.searchText.includes(stripSearchText(input));
                    }}
                    formatOptionLabel={(opt) => (
                      <div className="py-0.5">
                        <div className="font-semibold text-gray-800">{opt.roleName}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          <span className="font-medium text-gray-600">{opt.deptName}</span>
                          <span className="mx-1.5 text-gray-300">·</span>
                          <span>{opt.branchName}</span>
                        </div>
                      </div>
                    )}
                  />
                  <HintBox>
                    Sao chép <strong>toàn bộ quyền chức năng + phạm vi</strong> từ quyền gốc. Tên, chi
                    nhánh và bộ phận ở bước 1–2 vẫn do bạn đặt cho quyền mới.
                  </HintBox>
                </div>
              )}

              {source === "manual" && (
                <HintBox>
                  Bạn sẽ tự bật từng quyền ở bước 4. Nên dùng mẫu mặc định nếu không chắc cần cấp quyền
                  gì.
                </HintBox>
              )}
            </FormSection>
          )}

          {/* Bước 4: Quyền chi tiết */}
          <FormSection
            step={editing ? 3 : 4}
            title="Quyền & phạm vi theo nhóm chức năng"
            hint="Bật C/R/U/D/E/A/I và chọn phạm vi dữ liệu riêng cho từng màn hình (CRM rộng, Tài chính hẹp, …)."
            disabled={!orgReady}
          >
            <HintBox>
              <strong>C</strong> Tạo · <strong>R</strong> Xem · <strong>U</strong> Sửa · <strong>D</strong>{" "}
              Xóa · <strong>E</strong> Xuất · <strong>A</strong> Duyệt · <strong>I</strong> Dữ liệu nhạy cảm
              — cột <strong>Phạm vi</strong> quyết định NV nhìn thấy dữ liệu đến đâu trên màn đó.
            </HintBox>

            <div className="space-y-2">
              {MODULE_CATEGORIES.map((cat) => {
                const groups = FEATURE_GROUP_CATALOG.filter((g) => g.category === cat.key);
                const expanded = expandedCats[cat.key];
                const grantedCount = groups.reduce(
                  (n, g) => n + (grants[g.key]?.length || 0),
                  0
                );

                return (
                  <div key={cat.key} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-100">
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat.key)}
                        className="flex flex-1 min-w-[160px] items-center gap-2 text-left"
                      >
                        {expanded ? (
                          <ChevronDown size={16} className="text-gray-400" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-400" />
                        )}
                        <span className="text-sm font-bold text-gray-800">{cat.label}</span>
                        <span className="text-xs text-gray-400">({groups.length} màn hình)</span>
                        {grantedCount > 0 && (
                          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            {grantedCount} quyền bật
                          </span>
                        )}
                      </button>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] font-bold uppercase text-gray-400">Phạm vi nhóm</span>
                        <select
                          className="text-xs border rounded-lg px-2 py-1 bg-white"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) setCategoryScope(cat.key, e.target.value);
                            e.target.value = "";
                          }}
                          title="Áp dụng phạm vi cho mọi chức năng đang bật trong nhóm"
                        >
                          <option value="">Áp dụng…</option>
                          {SCOPE_LEVEL_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCategoryAll(cat.key, true)}
                        className="text-[10px] font-bold text-red-600 hover:underline px-1"
                      >
                        Bật hết
                      </button>
                      <button
                        type="button"
                        onClick={() => setCategoryAll(cat.key, false)}
                        className="text-[10px] font-bold text-gray-400 hover:underline px-1"
                      >
                        Tắt hết
                      </button>
                    </div>

                    {expanded && (
                      <div className="divide-y divide-gray-50">
                        {groups.map((group) => {
                          const hasGrant = (grants[group.key] || []).length > 0;
                          return (
                            <div key={group.key} className="px-3 py-3">
                              <div className="mb-2 flex flex-col sm:flex-row sm:items-start gap-2 sm:justify-between">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-gray-700">{group.labelVi}</p>
                                  <p className="text-xs text-gray-400">{group.description}</p>
                                </div>
                                <div className="shrink-0 sm:w-44">
                                  <label className="text-[10px] font-bold uppercase text-gray-400">
                                    Phạm vi dữ liệu
                                  </label>
                                  <select
                                    disabled={!hasGrant}
                                    className="mt-0.5 w-full text-xs border rounded-lg px-2 py-1.5 bg-white disabled:opacity-40 disabled:bg-gray-50"
                                    value={
                                      hasGrant
                                        ? moduleScopes[group.key] || scopeLevel
                                        : ""
                                    }
                                    onChange={(e) =>
                                      setFeatureScope(group.key, e.target.value)
                                    }
                                  >
                                    {!hasGrant && <option value="">Bật quyền trước</option>}
                                    {SCOPE_LEVEL_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {group.actions.map((action) => {
                                  const on = (grants[group.key] || []).includes(action);
                                  return (
                                    <button
                                      key={action}
                                      type="button"
                                      onClick={() => toggleAction(group.key, action)}
                                      title={ACTION_LABELS[action]}
                                      className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-all ${
                                        on
                                          ? "bg-red-600 text-white border-red-600"
                                          : "bg-white text-gray-500 border-gray-200 hover:border-red-200"
                                      }`}
                                    >
                                      {action} · {ACTION_LABELS[action] || action}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </FormSection>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 py-4 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400 hidden sm:block">
            {!orgReady ? (
              <span className="flex items-center gap-1">
                <Building2 size={12} /> Chọn chi nhánh ở bước 1 để tiếp tục
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Layers size={12} /> {branchId && departments.find((d) => d.value === departmentId)?.label
                  ? `${branches.find((b) => b.value === branchId)?.label} → ${departments.find((d) => d.value === departmentId)?.label}`
                  : branches.find((b) => b.value === branchId)?.label}
              </span>
            )}
          </p>
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              disabled={saving || busy.open}
              className="px-4 py-2 rounded-xl border text-sm font-medium disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              type="button"
              disabled={saving || busy.open || !orgReady}
              onClick={handleSave}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-bold disabled:opacity-50"
            >
              {(saving || busy.open) && <Loader2 size={14} className="animate-spin" />}
              {saving || busy.open ? "Đang lưu..." : "Lưu quyền sử dụng"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Modal chọn quyền giống 100% (trước khi sửa) để đồng bộ cấu hình. */
const UsageRoleSyncPeersModal = ({
  open,
  sourceId,
  sourceLabel,
  candidates,
  onClose,
  onDone,
}) => {
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState({
    open: false,
    percent: 0,
    title: "",
    detail: "",
  });

  useEffect(() => {
    if (!open) return;
    setSelected(new Set((candidates || []).map((c) => c.id)));
    setBusy({ open: false, percent: 0, title: "", detail: "" });
  }, [open, candidates]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(candidates.map((c) => c.id)) : new Set());
  };

  const handleConfirm = async () => {
    const targetIds = [...selected];
    if (!sourceId || !targetIds.length) {
      onClose?.();
      return;
    }

    setSaving(true);
    setBusy({
      open: true,
      percent: 0,
      title: "Đang đồng bộ quyền...",
      detail: `0 / ${targetIds.length}`,
    });

    let successCount = 0;
    const failed = [];
    try {
      for (let i = 0; i < targetIds.length; i += 1) {
        const targetId = targetIds[i];
        const meta = candidates.find((c) => c.id === targetId);
        const label = meta?.label || `Quyền ${i + 1}`;
        setBusy({
          open: true,
          percent: Math.round((i / targetIds.length) * 100),
          title: "Đang đồng bộ quyền...",
          detail: `${i + 1}/${targetIds.length}: ${label}`,
        });
        try {
          await propagateUsageRole(sourceId, [targetId]);
          successCount += 1;
        } catch {
          failed.push(label);
        }
        setBusy({
          open: true,
          percent: Math.round(((i + 1) / targetIds.length) * 100),
          title: "Đang đồng bộ quyền...",
          detail: `${i + 1}/${targetIds.length}: ${label}`,
        });
      }

      setBusy({
        open: true,
        percent: 100,
        title: "Hoàn tất đồng bộ",
        detail: `Đã cập nhật ${successCount}/${targetIds.length} quyền`,
      });
      await sleep(350);

      if (failed.length && successCount === 0) {
        showErrorToast("Không đồng bộ được quyền nào");
      } else if (failed.length) {
        showErrorToast(
          `Đồng bộ ${successCount} quyền; lỗi: ${failed.slice(0, 2).join(", ")}`
        );
      } else {
        showSuccessToast(`Đã đồng bộ ${successCount} quyền tương tự`);
      }
      onDone?.(successCount);
    } catch (err) {
      showErrorToast(err?.response?.data?.error || "Không đồng bộ được");
    } finally {
      setBusy({ open: false, percent: 0, title: "", detail: "" });
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[220] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="relative bg-white w-full sm:max-w-lg max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col">
        <BusyProgressOverlay
          open={busy.open}
          percent={busy.percent}
          title={busy.title}
          detail={busy.detail}
        />
        <div className="px-5 py-4 border-b">
          <h3 className="font-bold text-gray-800">Đồng bộ quyền tương tự?</h3>
          <p className="text-sm text-gray-500 mt-1">
            Phát hiện <strong>{candidates.length}</strong> quyền giống 100% cấu hình với{" "}
            <strong>{sourceLabel}</strong> (trước khi sửa). Chọn quyền muốn cập nhật theo cấu hình
            mới — tên / chi nhánh / bộ phận của từng quyền được giữ nguyên.
          </p>
        </div>

        <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              disabled={saving || busy.open}
              checked={selected.size === candidates.length && candidates.length > 0}
              onChange={(e) => toggleAll(e.target.checked)}
            />
            Chọn tất cả
          </label>
          <span className="text-xs text-gray-400">
            Đã chọn {selected.size}/{candidates.length}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {candidates.map((c) => (
            <label
              key={c.id}
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                selected.has(c.id)
                  ? "border-red-200 bg-red-50/40"
                  : "border-gray-100 hover:border-gray-200"
              } ${saving || busy.open ? "pointer-events-none opacity-70" : ""}`}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={selected.has(c.id)}
                onChange={() => toggle(c.id)}
              />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-800 text-sm">{c.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {c.department_name || "Mọi bộ phận"}
                  <span className="mx-1.5 text-gray-300">·</span>
                  {c.branch_name || "Toàn hệ thống"}
                </div>
                {c.is_clone_related && (
                  <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100">
                    Liên quan sao chép
                  </span>
                )}
              </div>
            </label>
          ))}
        </div>

        <div className="px-5 py-4 border-t flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || busy.open}
            className="px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50"
          >
            Bỏ qua
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving || busy.open || selected.size === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 text-sm font-semibold"
          >
            {(saving || busy.open) && <Loader2 size={14} className="animate-spin" />}
            {saving || busy.open
              ? `Đang đồng bộ ${busy.percent || 0}%`
              : `Đồng bộ ${selected.size} quyền`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function UsageRolePage() {
  const [rows, setRows] = useState([]);
  const [presets, setPresets] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cloneSource, setCloneSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsActionFilter, setLogsActionFilter] = useState("");
  const [logsLoading, setLogsLoading] = useState(true);
  const [syncPrompt, setSyncPrompt] = useState(null); // { sourceId, sourceLabel, candidates }
  const logsPageSize = 10;

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await getUsageRoleStats();
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await getUsageRoleLogs({
        page: logsPage,
        pageSize: logsPageSize,
        action: logsActionFilter || undefined,
      });
      setLogs(res?.data || []);
      setLogsTotal(res?.total || 0);
    } catch {
      setLogs([]);
      setLogsTotal(0);
    } finally {
      setLogsLoading(false);
    }
  }, [logsPage, logsActionFilter]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchFilter) params.branch_id = branchFilter;
      if (deptFilter) params.department_id = deptFilter;
      if (keyword) params.keyword = keyword;

      const load = (cfg) => getUsageRoles(params, cfg);
      let res;
      try {
        res = await load({ skipGlobalErrorToast: true });
      } catch (err) {
        // Phiên cũ thiếu permission mới → refresh JWT từ DB rồi thử lại 1 lần
        if (err?.response?.status === 403) {
          const ok = await refreshSessionPermissions();
          if (ok) {
            res = await load();
          } else {
            throw err;
          }
        } else {
          throw err;
        }
      }
      setRows(res?.data || []);
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg =
        err?.response?.data?.message || err?.response?.data?.error;
      if (status === 403) {
        showErrorToast(
          serverMsg ||
            "Không có quyền xem danh sách. Đăng xuất rồi đăng nhập lại để cập nhật quyền."
        );
      } else {
        showErrorToast(serverMsg || "Không tải được danh sách");
      }
    } finally {
      setLoading(false);
    }
  }, [branchFilter, deptFilter, keyword]);

  const refreshAll = useCallback(() => {
    fetchList();
    fetchStats();
    fetchLogs();
  }, [fetchList, fetchStats, fetchLogs]);

  useEffect(() => {
    getUsageRolePresets().then(setPresets).catch(() => {});
    getAllBranches_Array().then((b) =>
      setBranches(b.map((x) => ({ value: x.id, label: x.name })))
    );
  }, []);

  useEffect(() => {
    if (!branchFilter) {
      setDepartments([]);
      return;
    }
    getDepartmentsByBranch(branchFilter).then((d) =>
      setDepartments(d.map((x) => ({ value: x.id, label: x.name })))
    );
  }, [branchFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const scopeLabel = useMemo(() => {
    const map = Object.fromEntries(SCOPE_LEVEL_OPTIONS.map((o) => [o.value, o.label]));
    return (v) => map[v] || v;
  }, []);

  const handleDelete = async (row) => {
    if (!window.confirm(`Xóa quyền "${row.label}"?`)) return;
    try {
      await deleteUsageRole(row.id);
      showSuccessToast("Đã xóa");
      refreshAll();
    } catch (err) {
      showErrorToast(err?.response?.data?.error || "Không xóa được");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Shield className="text-red-600" size={22} />
            Quản lý quyền sử dụng
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Tạo quyền theo chi nhánh → bộ phận → cấu hình chức năng. Gán cho NV khi tạo hồ sơ.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setCloneSource(null);
            setModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold"
        >
          <Plus size={16} />
          Thêm quyền
        </button>
      </div>

      <UsageRoleStatsBar stats={stats} loading={statsLoading} />

      <div className="bg-white border rounded-2xl p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[160px] flex-1">
          <label className="text-[10px] font-bold uppercase text-gray-400">Chi nhánh</label>
          <Select
            styles={customSelectStyles}
            isClearable
            options={branches}
            value={branches.find((b) => b.value === branchFilter) || null}
            onChange={(opt) => {
              setBranchFilter(opt?.value || "");
              setDeptFilter("");
            }}
          />
        </div>
        <div className="min-w-[160px] flex-1">
          <label className="text-[10px] font-bold uppercase text-gray-400">Bộ phận</label>
          <Select
            styles={customSelectStyles}
            isClearable
            options={departments}
            value={departments.find((d) => d.value === deptFilter) || null}
            onChange={(opt) => setDeptFilter(opt?.value || "")}
            isDisabled={!branchFilter}
          />
        </div>
        <div className="min-w-[180px] flex-1">
          <label className="text-[10px] font-bold uppercase text-gray-400">Tìm kiếm</label>
          <input
            className="w-full border rounded-xl px-3 py-2 text-sm mt-0.5"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Tên quyền..."
          />
        </div>
        <button
          type="button"
          onClick={fetchList}
          className="p-2.5 border rounded-xl hover:bg-gray-50"
          title="Tải lại"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="bg-white border rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Quyền sử dụng</th>
              <th className="px-4 py-3">Chi nhánh</th>
              <th className="px-4 py-3">Bộ phận</th>
              <th className="px-4 py-3">Phạm vi</th>
              <th className="px-4 py-3 text-center">NV gán</th>
              <th className="px-4 py-3 text-right">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  {loading ? "Đang tải..." : "Chưa có quyền sử dụng"}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-medium text-gray-800">{row.label}</td>
                  <td className="px-4 py-3 text-gray-600">{row.branch_name || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{row.department_name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold px-2 py-1 rounded-lg bg-red-50 text-red-700">
                      {scopeLabel(row.scope_level)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">{row.assigned_count ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        title="Sửa"
                        onClick={() => {
                          setEditing(row);
                          setCloneSource(null);
                          setModalOpen(true);
                        }}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        title="Nhân bản"
                        onClick={() => {
                          setEditing(null);
                          setCloneSource(row);
                          setModalOpen(true);
                        }}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                      >
                        <Copy size={15} />
                      </button>
                      <button
                        type="button"
                        title="Xóa"
                        onClick={() => handleDelete(row)}
                        className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UsageRoleLogsPanel
        logs={logs}
        total={logsTotal}
        page={logsPage}
        pageSize={logsPageSize}
        actionFilter={logsActionFilter}
        loading={logsLoading}
        onPageChange={setLogsPage}
        onFilterChange={(v) => {
          setLogsActionFilter(v);
          setLogsPage(1);
        }}
      />

      <UsageRoleFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(updated) => {
          refreshAll();
          if (updated?.sync_candidates?.length) {
            setSyncPrompt({
              sourceId: updated.id,
              sourceLabel: updated.label,
              candidates: updated.sync_candidates,
            });
          }
        }}
        editing={editing}
        cloneSource={cloneSource}
        presets={presets}
      />

      <UsageRoleSyncPeersModal
        open={!!syncPrompt}
        sourceId={syncPrompt?.sourceId}
        sourceLabel={syncPrompt?.sourceLabel}
        candidates={syncPrompt?.candidates || []}
        onClose={() => setSyncPrompt(null)}
        onDone={() => {
          setSyncPrompt(null);
          refreshAll();
        }}
      />
    </div>
  );
}
